import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core';
import { getEventCoordinates } from '@dnd-kit/utilities';
import { useCallback, useEffect, useState } from 'react';

import { useTabContentConfig } from '../../PageConfigProvider/PageConfigProvider';
import type {
  Position,
  PositionedItem,
} from '../../PageConfigProvider/types/item';
import { useGrid } from '../GridProvider';

interface UseEditableGridOptions {
  onBuilderOpenChange?: (isOpen: boolean) => void;
}

interface UseEditableGridReturn {
  openConfigId: string | null;
  closeConfig: () => void;
  selectedIds: Set<string>;
  clearSelection: () => void;
  handleChartClick: (id: string, event: React.MouseEvent) => void;
  filteredGridItems: PositionedItem[];
  selectedItem: PositionedItem | undefined;
  handleAddItem: (position: Position) => void;
  handleDragStart: (event: DragStartEvent) => void;
  handleDragEnd: (event: DragEndEvent) => void;
}

export const useEditableGrid = ({
  onBuilderOpenChange,
}: UseEditableGridOptions): UseEditableGridReturn => {
  const {
    gridItems,
    itemDragging,
    setItemDragging,
    updateItemPosition,
    updateItemPositions,
  } = useGrid();
  const { dispatch } = useTabContentConfig();

  const [openConfigId, setOpenConfigId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const selectedItem = gridItems.find((item) => item.id === openConfigId);

  const openConfig = (id: string) => {
    setOpenConfigId(id);
    setSelectedIds(new Set([id]));
    onBuilderOpenChange?.(true);
  };

  const closeConfig = () => {
    setOpenConfigId(null);
    onBuilderOpenChange?.(false);
  };

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const handleChartClick = (id: string, event: React.MouseEvent) => {
    if (event.shiftKey) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) {
          next.delete(id);
        } else {
          next.add(id);
        }
        return next;
      });
    } else {
      setSelectedIds(new Set([id]));
      setOpenConfigId(id);
      onBuilderOpenChange?.(true);
    }
  };

  useEffect(() => {
    if (openConfigId === null) return;
    const stillExists = gridItems.some((item) => item.id === openConfigId);
    if (!stillExists) {
      setOpenConfigId(null);
      onBuilderOpenChange?.(false);
    }
  }, [gridItems, openConfigId, onBuilderOpenChange]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: migrated from develop
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Delete' && event.key !== 'Backspace') return;
      const target = event.target as Node;
      if (
        target &&
        (target instanceof HTMLInputElement ||
          target instanceof HTMLTextAreaElement ||
          (target instanceof HTMLElement && target.isContentEditable))
      ) {
        return;
      }
      if (selectedIds.size === 0) return;
      event.preventDefault();
      dispatch({
        type: 'DELETE_ITEMS',
        payload: { ids: Array.from(selectedIds) },
      });
      setSelectedIds(new Set());
      closeConfig();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedIds, dispatch]);

  const handleAddItem = (position: Position) => {
    const id = crypto.randomUUID();
    dispatch({ type: 'ADD_BLANK_ITEM', payload: { position, id } });
    openConfig(id);
  };

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const activeItem = active?.data?.current as PositionedItem | undefined;
    if (!activeItem) return;
    setItemDragging(activeItem);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const activeItem = itemDragging;
    if (!activeItem) return;
    const activationCoordinates = getEventCoordinates(event.activatorEvent);
    const pointerCoordinates = activationCoordinates
      ? {
          x: activationCoordinates.x + event.delta.x,
          y: activationCoordinates.y + event.delta.y,
        }
      : null;

    if (selectedIds.has(activeItem.id) && selectedIds.size > 1) {
      const itemsToMove = gridItems.filter((item) => selectedIds.has(item.id));
      if (itemsToMove.length > 0) {
        updateItemPositions({ items: itemsToMove, delta: event.delta });
      }
    } else {
      updateItemPosition({
        item: activeItem,
        delta: event.delta,
        pointerCoordinates,
      });
    }

    setItemDragging(null);
  };

  return {
    openConfigId,
    closeConfig,
    selectedIds,
    clearSelection,
    handleChartClick,
    filteredGridItems: gridItems,
    selectedItem,
    handleAddItem,
    handleDragStart,
    handleDragEnd,
  };
};
